import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plus, Package, Clock, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UserDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orders`, {
        withCredentials: true,
      });
      setOrders(data);
    } catch (error) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { label: 'Pendiente', icon: <Clock size={16} />, className: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' },
      processing: { label: 'Procesando', icon: <Package size={16} />, className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
      completed: { label: 'Completado', icon: <CheckCircle size={16} />, className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
      failed: { label: 'Fallido', icon: <XCircle size={16} />, className: 'bg-red-500/10 text-red-600 dark:text-red-400' },
    };

    const variant = variants[status] || variants.pending;

    return (
      <Badge className={`${variant.className} flex items-center gap-1`}>
        {variant.icon}
        {variant.label}
      </Badge>
    );
  };

  const formatARS = (value) => `$${value.toLocaleString('es-AR')}`;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="dashboard-title">
              Mis Pedidos
            </h1>
            <p className="text-muted-foreground">
              Administra y rastrea todos tus pedidos
            </p>
          </div>
          <Link to="/dashboard/new-order" data-testid="create-order-button">
            <Button size="lg" className="flex items-center gap-2">
              <Plus size={20} weight="bold" />
              Nuevo Pedido
            </Button>
          </Link>
        </div>

        {orders.length === 0 ? (
          <Card className="p-12 text-center border-border" data-testid="no-orders-message">
            <Package size={64} className="mx-auto mb-4 text-muted-foreground" weight="duotone" />
            <h3 className="text-xl font-semibold mb-2">No tienes pedidos aún</h3>
            <p className="text-muted-foreground mb-6">
              Comienza creando tu primer pedido
            </p>
            <Link to="/dashboard/new-order">
              <Button>
                <Plus size={20} className="mr-2" />
                Crear Pedido
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card
                key={order.id}
                className="p-6 border-border hover:-translate-y-1 transition-all duration-200"
                data-testid={`order-card-${order.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{order.service_name}</h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Plataforma: <span className="font-medium">{order.platform}</span>
                    </p>
                    <p className="text-sm text-muted-foreground mb-1">
                      Cantidad: <span className="font-medium">{order.quantity}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Fecha: <span className="font-medium">{formatDate(order.created_at)}</span>
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold">{formatARS(order.price)}</p>
                      {order.discount > 0 && (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          Descuento: {formatARS(order.discount)}
                        </p>
                      )}
                    </div>
                    <Link to={`/dashboard/orders/${order.id}`}>
                      <Button variant="outline" size="sm">
                        Ver Detalles
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};